import { useSubjectMember } from '@healthblocks-io/core'
import { useMemo } from 'react'

import {
  selectCanGoBack,
  selectCanGoNext,
  selectCanSubmit,
  selectCurrentGlobalIndex,
  selectCurrentQuestion,
  selectCurrentQuestionIndex,
  selectMaxPossibleQuestions,
  selectTotalQuestions,
} from '../providers/questionnaireEngine.selectors'
import { useQuestionnaireEngine } from './useQuestionnaireEngine'

import type { IQuestionnaireQuestion, QuestionAnswerInput } from '@healthblocks-io/core'
import type { QuestionnaireEngineState } from '../types/questionnaire-engine.types'
import type { QuestionnaireFlowStrategy } from './questionnaire-flow-strategy'

export type QuestionnaireFlowResult =
  | { type: 'MOVED' }
  | { type: 'COMPLETED' }
  | { type: 'BLOCKED' }
  | { type: 'ERROR' }
  | { type: 'IGNORED' }
  | { type: 'ANSWERED' }

/**
 * useQuestionnaireFlow
 *
 * Unified hook for managing questionnaire navigation across all flow modes.
 * The navigation policy is determined by the provided strategy:
 *
 * - **linearStrategy**: Sequential progression (normal questionnaire flow).
 *   Saves progress on each navigation, submits on the last question.
 *
 * - **invalidFirstStrategy**: Jumps to invalid questions first (reconfirm/prefill flows).
 *   Saves only on completion, with wrap-around search for next invalid question.
 *
 * @param strategy - Determines navigation policy, save behavior, and completion logic
 */
export function useQuestionnaireFlow(strategy: QuestionnaireFlowStrategy): {
  state: QuestionnaireEngineState
  currentIndex: number
  currentQuestion: IQuestionnaireQuestion | null
  totalQuestions: number
  /** Count of non-Display items in questionnaire.item — constant denominator for linear-flow progress */
  maxPossibleQuestions: number
  /** 0-based position of the current question in the full questionnaire (skipping Display items) — numerator for linear-flow progress */
  globalIndex: number
  canGoBack: boolean
  canGoNext: boolean
  canSubmit: boolean
  goNext: () => Promise<QuestionnaireFlowResult>
  goBack: () => QuestionnaireFlowResult
  setAnswer: (
    answer: QuestionAnswerInput & { fileIds?: number[] },
    autoAdvance?: boolean,
  ) => Promise<QuestionnaireFlowResult>
} {
  const subject = useSubjectMember()

  const {
    state,
    navigate: engineNavigate,
    goBack: engineGoBack,
    setAnswer: engineSetAnswer,
    saveProgress,
    submitQuestionnaire,
  } = useQuestionnaireEngine()

  const derived = useMemo(() => {
    const currentIndex = selectCurrentQuestionIndex(state)
    const currentQuestion = selectCurrentQuestion(state)
    const totalQuestions = selectTotalQuestions(state)
    const maxPossibleQuestions = selectMaxPossibleQuestions(state)
    const globalIndex = selectCurrentGlobalIndex(state)
    return {
      currentIndex,
      currentQuestion,
      totalQuestions,
      maxPossibleQuestions,
      globalIndex,
      canSubmit: selectCanSubmit(state),
      canGoBack: selectCanGoBack(state),
      canGoNext: selectCanGoNext(state),
    }
  }, [state])

  async function completeFlow(answers: Record<string, QuestionAnswerInput>): Promise<QuestionnaireFlowResult> {
    try {
      await (strategy.completeAction === 'submit' ? submitQuestionnaire(answers) : saveProgress(answers))
      return { type: 'COMPLETED' }
    } catch (error) {
      console.error('Error completing questionnaire flow:', error)
      return { type: 'ERROR' }
    }
  }

  function goBack(): QuestionnaireFlowResult {
    if (!derived.canGoBack) return { type: 'BLOCKED' }
    engineGoBack()
    return { type: 'MOVED' }
  }

  async function goNext(): Promise<QuestionnaireFlowResult> {
    if (!subject) return { type: 'IGNORED' }

    if (strategy.isComplete(state, derived)) {
      return completeFlow(state.answers)
    }

    const nextLinkId = strategy.findNext(state)
    if (!nextLinkId) {
      // Defensive: if no next found in on-complete mode, treat as completed
      if (strategy.saveBehavior === 'on-complete') {
        return completeFlow(state.answers)
      }
      return { type: 'BLOCKED' }
    }

    if (strategy.saveBehavior === 'on-navigate') {
      await saveProgress(state.answers)
    }

    engineNavigate(nextLinkId, 'push')
    return { type: 'MOVED' }
  }

  async function setAnswer(
    answer: QuestionAnswerInput & { fileIds?: number[] },
    autoAdvance?: boolean,
  ): Promise<QuestionnaireFlowResult> {
    if (!subject) return { type: 'IGNORED' }

    engineSetAnswer(answer)

    if (!autoAdvance) return { type: 'ANSWERED' }

    const { isComplete, nextLinkId } = strategy.resolveAutoAdvance(state, subject, answer, derived)

    const nextAnswers = { ...state.answers, [answer.linkId]: answer }

    if (isComplete) {
      return completeFlow(nextAnswers)
    }

    if (!nextLinkId) return { type: 'BLOCKED' }

    if (strategy.saveBehavior === 'on-navigate') {
      await saveProgress(nextAnswers)
    }

    engineNavigate(nextLinkId, 'push')
    return { type: 'MOVED' }
  }

  return {
    state,
    currentIndex: derived.currentIndex,
    currentQuestion: derived.currentQuestion,
    totalQuestions: derived.totalQuestions,
    maxPossibleQuestions: derived.maxPossibleQuestions,
    globalIndex: derived.globalIndex,
    canGoBack: derived.canGoBack,
    canGoNext: derived.canGoNext,
    canSubmit: derived.canSubmit,
    goNext,
    goBack,
    setAnswer,
  }
}
